import { useEffect, useRef, useState } from 'react';
import { useAppStore } from '@/store';
import { cn } from '@/lib/utils';
import { Search, Plus, Pin, Scale, Loader2, FileCog, X, AlertTriangle, Loader, LogOut, Users as UsersIcon, Sparkles, GraduationCap, MessageSquare, FileStack, Upload, FileText, Check, AlertCircle } from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import { format } from 'date-fns';
import { uk } from 'date-fns/locale';
import { SIMPLE, PROJECT_NAME } from '@/lib/config';
import { uploadTemplate } from '@/services/api';
import type { AppView } from '@/App';

interface TemplateUploadItem {
  id: string;
  filename: string;
  status: 'uploading' | 'done' | 'error';
  error?: string;
}

const STATE_COLORS: Record<string, string> = {
  INTAKE: 'bg-blue-500/20 text-blue-400',
  STORY_COLLECTION: 'bg-amber-500/20 text-amber-400',
  DATA_COLLECTION: 'bg-amber-500/20 text-amber-400',
  GENERATION: 'bg-emerald-500/20 text-emerald-400',
  DELIVERY: 'bg-emerald-600/30 text-emerald-300',
};

const STATE_LABELS: Record<string, string> = {
  INTAKE: 'Вступ',
  STORY_COLLECTION: 'Збір даних',
  DATA_COLLECTION: 'Деталі',
  GENERATION: 'Готово',
  DELIVERY: 'Видано',
};

export function Sidebar({ view, onViewChange }: { view?: AppView; onViewChange?: (v: AppView) => void }) {
  const { cases, activeCaseId, loadingCases, fetchSessions, setActiveCaseId, startNewCase, setAdminOpen, deleteCase, setMobileNavOpen } = useAppStore();
  const [search, setSearch] = useState('');
  const [askingDeleteId, setAskingDeleteId] = useState<string | null>(null);
  const [confirmModalCase, setConfirmModalCase] = useState<typeof cases[0] | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [templateQueue, setTemplateQueue] = useState<TemplateUploadItem[]>([]);
  const didFetch = useRef(false);
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!didFetch.current) {
      didFetch.current = true;
      fetchSessions();
    }
  }, [fetchSessions]);

  // Close inline ask on outside click / escape
  useEffect(() => {
    if (!askingDeleteId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAskingDeleteId(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [askingDeleteId]);

  const filtered = cases.filter((c) =>
    c.title.toLowerCase().includes(search.toLowerCase())
  );

  const handleTemplateUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.docx,.doc';
    input.onchange = async (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (!files.length) return;
      const items: TemplateUploadItem[] = files.map((f) => ({
        id: Math.random().toString(36).slice(2),
        filename: f.name,
        status: 'uploading',
      }));
      setTemplateQueue((q) => [...q, ...items]);
      for (let i = 0; i < files.length; i++) {
        const itemId = items[i].id;
        try {
          await uploadTemplate(files[i]);
          setTemplateQueue((q) => q.map((it) => it.id === itemId ? { ...it, status: 'done' } : it));
        } catch (err: any) {
          setTemplateQueue((q) => q.map((it) => it.id === itemId ? { ...it, status: 'error', error: err?.message } : it));
        }
      }
      setTimeout(() => {
        setTemplateQueue((q) => q.filter((it) => it.status !== 'done'));
      }, 4000);
    };
    input.click();
  };

  const pinned = filtered.filter((c) => (c as any).pinned);
  const others = filtered.filter((c) => !(c as any).pinned);

  const handleConfirmFinal = async () => {
    if (!confirmModalCase) return;
    const id = confirmModalCase.id;
    setDeletingId(id);
    try {
      await deleteCase(id);
      setConfirmModalCase(null);
    } catch (err: any) {
      alert('Помилка: ' + (err?.message || 'не вдалося видалити справу'));
    } finally {
      setDeletingId(null);
    }
  };

  const renderItem = (c: typeof cases[0]) => {
    const asking = askingDeleteId === c.id;
    return (
      <div key={c.id} className="relative group">
        <button
          onClick={() => {
            if (asking) return;
            setActiveCaseId(c.id);
            setMobileNavOpen(false);
          }}
          className={cn(
            'w-full text-left pl-3 pr-9 py-2.5 rounded-xl flex items-start gap-2.5 transition-all border',
            activeCaseId === c.id
              ? 'bg-white/10 border-white/15'
              : 'border-transparent hover:bg-white/5'
          )}
        >
          <div className="w-2 h-2 mt-1.5 rounded-full shrink-0 bg-blue-500" />
          <div className="flex-1 overflow-hidden">
            <div
              className={cn(
                'text-sm font-medium truncate',
                activeCaseId === c.id ? 'text-white' : 'text-white/80'
              )}
            >
              {c.title}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              {c.state && (
                <span
                  className={cn(
                    'text-[9px] font-bold uppercase px-1.5 py-0.5 rounded',
                    STATE_COLORS[c.state] ?? 'bg-white/10 text-white/40'
                  )}
                >
                  {STATE_LABELS[c.state] ?? c.state}
                </span>
              )}
              <span className="text-[10px] text-white/30">
                {format(new Date(c.updatedAt), 'dd MMM, HH:mm', { locale: uk })}
              </span>
            </div>
          </div>
        </button>

        {/* Hover delete button */}
        {!asking && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setAskingDeleteId(c.id);
            }}
            className="absolute top-1.5 right-1.5 p-1 rounded-md text-white/30 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
            title="Видалити справу"
          >
            <X className="h-4 w-4" />
          </button>
        )}

        {/* Step 1: inline confirmation */}
        {asking && (
          <div
            className="absolute inset-0 z-10 rounded-xl bg-[#181820] border border-amber-500/40 shadow-lg shadow-black/40 flex flex-col p-2.5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-xs text-white/90 leading-snug mb-2">
              <span className="text-amber-400 font-semibold">Справа буде видалена.</span>
              <br />
              Продовжити?
            </div>
            <div className="flex gap-1.5">
              <button
                onClick={() => setAskingDeleteId(null)}
                className="flex-1 px-2 py-1 rounded bg-white/5 hover:bg-white/10 border border-white/10 text-[11px] text-white/80 transition-colors"
              >
                Ні
              </button>
              <button
                onClick={() => {
                  setAskingDeleteId(null);
                  setConfirmModalCase(c);
                }}
                className="flex-1 px-2 py-1 rounded bg-amber-600/30 hover:bg-amber-600/50 border border-amber-500/50 text-[11px] text-amber-200 font-semibold transition-colors"
              >
                Так
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className="flex-1 flex flex-col border-r border-white/10 bg-[var(--t-panel)] min-w-0">
      {/* Logo */}
      <div className="p-4 flex items-center gap-3 border-b border-white/5">
        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
          <Scale className="w-4 h-4 text-white" />
        </div>
        <span className="font-semibold tracking-tight text-white flex-1">{PROJECT_NAME}</span>
        <button
          onClick={() => setMobileNavOpen(false)}
          className="xl:hidden p-1.5 rounded-md text-white/40 hover:text-white hover:bg-white/5 transition-colors"
          title="Закрити"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* View navigation */}
      {onViewChange && (
        <div className="px-3 pt-3 pb-0">
          <div className="flex bg-white/5 border border-white/10 rounded-lg p-0.5 gap-0.5">
            <button
              onClick={() => onViewChange('chat')}
              className={cn(
                'flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded text-[11px] font-medium transition-colors',
                view === 'chat' || !view
                  ? 'bg-blue-600 text-white shadow'
                  : 'text-white/50 hover:text-white/80'
              )}
            >
              <MessageSquare className="w-3.5 h-3.5" />
              Чат
            </button>
            <button
              onClick={() => onViewChange('contracts')}
              className={cn(
                'flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded text-[11px] font-medium transition-colors',
                view === 'contracts'
                  ? 'bg-emerald-600 text-white shadow'
                  : 'text-white/50 hover:text-white/80'
              )}
            >
              <FileStack className="w-3.5 h-3.5" />
              Шаблони
            </button>
          </div>
        </div>
      )}

      {/* Controls — only visible in chat view */}
      <div className={cn('p-3 space-y-2', view === 'contracts' && 'hidden')}>
        <button
          onClick={() => { startNewCase(); setMobileNavOpen(false); }}
          className="w-full py-2 px-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm flex items-center justify-center gap-2 transition-colors text-white"
        >
          <Plus className="h-4 w-4" />
          Нова справа
        </button>
        {!SIMPLE && (
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-white/40" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Пошук по справах..."
              className="w-full pl-9 pr-3 h-9 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-blue-600/50"
            />
          </div>
        )}
      </div>

      {/* Template upload zone — contracts view, admin only */}
      {view === 'contracts' && isAdmin && (
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-0 scrollbar-thin scrollbar-thumb-white/10">
          <div className="text-[10px] font-bold text-white/30 uppercase tracking-widest px-1">
            Завантажити в шаблони
          </div>
          <button
            onClick={handleTemplateUpload}
            className="w-full py-2.5 px-3 bg-white/5 border border-dashed border-white/20 rounded-xl text-xs text-white/50 hover:bg-white/10 hover:text-white/80 hover:border-white/30 transition-colors flex items-center justify-center gap-2"
          >
            <Upload className="h-3.5 w-3.5" />
            Вибрати DOCX файли
          </button>
          {templateQueue.length > 0 && (
            <div className="space-y-1.5">
              {templateQueue.map((item) => (
                <div key={item.id} className={cn(
                  'flex items-center gap-2 p-2 rounded-lg border text-[11px]',
                  item.status === 'done' ? 'bg-emerald-900/20 border-emerald-500/25 text-emerald-300' :
                  item.status === 'error' ? 'bg-red-900/20 border-red-500/25 text-red-300' :
                  'bg-white/5 border-white/10 text-white/60'
                )}>
                  <FileText className="h-3.5 w-3.5 shrink-0" />
                  <span className="flex-1 truncate">{item.filename}</span>
                  {item.status === 'uploading' && <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />}
                  {item.status === 'done' && <Check className="h-3.5 w-3.5 shrink-0" />}
                  {item.status === 'error' && <AlertCircle className="h-3.5 w-3.5 shrink-0" />}
                </div>
              ))}
            </div>
          )}
          {templateQueue.length === 0 && (
            <p className="text-[10px] text-white/20 text-center leading-relaxed px-2">
              Завантажені DOCX стануть шаблонами для генерації документів
            </p>
          )}
        </div>
      )}

      {/* Empty placeholder when in contracts view but not admin */}
      {view === 'contracts' && !isAdmin && (
        <div className="flex-1" />
      )}

      {/* Case list — hidden in contracts view */}
      <div className={cn('flex-1 overflow-y-auto px-2 py-1 space-y-1 scrollbar-thin scrollbar-thumb-white/10', view === 'contracts' && 'hidden')}>
        {loadingCases && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 text-white/30 animate-spin" />
          </div>
        )}

        {!loadingCases && filtered.length === 0 && (
          <div className="text-center py-10 text-white/30 text-xs">
            {cases.length === 0 ? 'Ще немає справ' : 'Нічого не знайдено'}
          </div>
        )}

        {pinned.length > 0 && (
          <div className="mb-2">
            <div className="text-[10px] uppercase tracking-widest text-white/30 px-3 py-1.5 flex items-center gap-1">
              <Pin className="w-3 h-3" /> Закріплені
            </div>
            <div className="space-y-0.5">{pinned.map(renderItem)}</div>
          </div>
        )}

        {others.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/30 px-3 py-1.5">
              Всі справи
            </div>
            <div className="space-y-0.5">{others.map(renderItem)}</div>
          </div>
        )}
      </div>

      {/* Step 2: full confirm modal */}
      {confirmModalCase && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[var(--t-panel)] border border-red-500/40 rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-bold text-white tracking-tight">
                  Я ПІДТВЕРДЖУЮ — СПРАВУ МОЖНА ВИДАЛИТИ
                </h3>
                <p className="text-xs text-white/40 mt-0.5">Дію неможливо скасувати.</p>
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-5">
              <div className="text-[10px] uppercase tracking-widest text-white/30 mb-1">Справа</div>
              <div className="text-sm text-white truncate">{confirmModalCase.title}</div>
              <div className="text-[10px] text-white/30 mt-1">
                Усі повідомлення, зібрані дані, завантажені та згенеровані файли будуть видалені без можливості відновлення.
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmModalCase(null)}
                disabled={deletingId === confirmModalCase.id}
                className="px-5 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white/80 transition-colors disabled:opacity-50"
              >
                НІ
              </button>
              <button
                onClick={handleConfirmFinal}
                disabled={deletingId === confirmModalCase.id}
                className="px-5 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 text-sm text-white font-bold tracking-wide transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deletingId === confirmModalCase.id && <Loader className="h-4 w-4 animate-spin" />}
                ТАК, ВИДАЛИТИ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <SidebarFooter setAdminOpen={setAdminOpen} />
    </aside>
  );
}

function SidebarFooter({ setAdminOpen }: { setAdminOpen: (v: boolean) => void }) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const setMode = useAuthStore((s) => s.setMode);
  const setUsersAdminOpen = useAppStore((s) => s.setUsersAdminOpen);
  const setMobileNavOpen = useAppStore((s) => s.setMobileNavOpen);
  const isAdmin = user?.role === 'admin';
  const isPro = user?.mode === 'pro';
  const initial = (user?.fullName?.[0] || user?.email?.[0] || 'U').toUpperCase();

  return (
    <div className="p-3 border-t border-white/10 bg-[var(--t-footer)] space-y-2">
      {/* Mode toggle — hidden in SIMPLE mode */}
      {!SIMPLE && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-1 flex items-center gap-0.5">
          <button
            onClick={() => setMode('novice')}
            title="Простий режим: бот не перепитує очевидне"
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-[11px] font-medium transition-colors',
              !isPro
                ? 'bg-blue-600 text-white shadow'
                : 'text-white/50 hover:text-white/80'
            )}
          >
            <GraduationCap className="w-3.5 h-3.5" />
            Новачок
          </button>
          <button
            onClick={() => setMode('pro')}
            title="Юрист: можу запитати про деталі, очікую структуровану інформацію"
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-[11px] font-medium transition-colors',
              isPro
                ? 'bg-amber-500 text-[#0a0a0c] shadow'
                : 'text-white/50 hover:text-white/80'
            )}
          >
            <Sparkles className="w-3.5 h-3.5" />
            Юрист
          </button>
        </div>
      )}

      {isAdmin && (
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => { setAdminOpen(true); setMobileNavOpen(false); }}
            className="flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-[11px] text-white/80 transition-colors"
          >
            <FileCog className="h-3.5 w-3.5" />
            Шаблони
          </button>
          <button
            onClick={() => { setUsersAdminOpen(true); setMobileNavOpen(false); }}
            className="flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-[11px] text-white/80 transition-colors"
          >
            <UsersIcon className="h-3.5 w-3.5" />
            Користувачі
          </button>
        </div>
      )}

      <div className="flex items-center gap-3 px-1 group">
        <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-500 to-yellow-400 shrink-0 flex items-center justify-center text-xs font-bold text-[#0a0a0c]">
          {initial}
        </div>
        <div className="flex-1 overflow-hidden min-w-0">
          <div className="text-xs font-medium text-white truncate" title={user?.fullName || user?.email}>
            {user?.fullName || user?.email || '—'}
          </div>
          <div className="text-[10px] text-white/30 uppercase tracking-tight truncate">
            {user?.fullName ? user.email : (isAdmin ? 'Адмін' : 'Користувач')}
          </div>
        </div>
        <button
          onClick={logout}
          title="Вийти"
          className="p-1.5 rounded-md text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
